import { styled } from "@mui/material";
import { TreeItem, treeItemClasses } from "@mui/x-tree-view-pro";
import React from "react";
import { breakpoints } from "../../theme/theme";

const StyledTreeItem = styled(TreeItem)(({ theme }) => ({
  // Root tree item styling - applies to all levels
  [`& .${treeItemClasses.content}`]: {
    padding: '4px 4px 4px 4px',
    borderRadius: '6px',
    margin: '2px 0 0 0',
    transition: 'all 0.2s ease',
    position: 'relative',
    border: '1px solid transparent',

    [`@media (min-width:${breakpoints.xl}px)`]: {
      padding: '3px 4px 3px 4px',
    },
    '&:hover': {
      backgroundColor: 'rgba(248, 250, 252, 0.8)', // slate-50/80
      borderColor: 'rgba(226, 232, 240, 0.5)', // slate-200/50
      boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    },
    '&:focus': {
      backgroundColor: 'rgba(239, 246, 255, 0.6)', // blue-50/60
      borderColor: 'rgba(191, 219, 254, 0.8)', // blue-200/80
      outline: 'none',
      boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    },
    '&.Mui-selected': {
      backgroundColor: '#eff6ff', // blue-50
      borderColor: 'rgba(191, 219, 254, 0.5)',
      boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
      '&:hover': {
        backgroundColor: '#eff6ff',
        borderColor: 'rgba(191, 219, 254, 0.5)',
      },
    },
    [`& .${treeItemClasses.label}`]: {
      fontWeight: 500,
      fontSize: '12px',
      color: '#1e293b',
      transition: 'color 0.2s ease',
    },
    '&:hover .MuiTreeItem-label': {
      color: '#0f172a',
    },
  },

  // Level-specific adjustments
  '&[data-level="0"]': {
    [`& .${treeItemClasses.content}`]: {
      padding: '12px',
      [`& .${treeItemClasses.label}`]: {
        fontWeight: 600,
      },
    },
    [`& .${treeItemClasses.groupTransition}`]: {
      '&::before': {
        display: 'none',
      },
      [`& > .${treeItemClasses.root}::before`]: {
        display: 'none',
      },
    },
  },

  [`&.Mui-selected .${treeItemClasses.content} .${treeItemClasses.label}`]: {
    color: '#1e3a8a',
  },

  [`& .${treeItemClasses.iconContainer}`]: {
    margin: 0,
    marginRight: '8px',
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
    backgroundColor: 'transparent',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    '& svg': {
      color: '#64748b',
      transition: 'color 0.2s ease',
    },
    '&.expanded svg': {
      color: '#2563eb',
    },
    '&:hover': {
      backgroundColor: '#f1f5f9',
      border: '1px solid #e2e8f0',
    },
  },

  [`&.Mui-expanded > .${treeItemClasses.content} .${treeItemClasses.iconContainer} svg`]: {
    color: '#2563eb',
  },

  [`& .${treeItemClasses.content}:hover .${treeItemClasses.iconContainer} svg`]: {
    color: '#2563eb',
  },

  [`& .${treeItemClasses.groupTransition}`]: {
    marginLeft: '8px',
    marginTop: '4px',
    paddingLeft: '12px',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: '8px',
      top: '0',
      bottom: '0',
      width: '1px',
      background: 'linear-gradient(to bottom, rgb(191 219 254), transparent)',
      opacity: 1,
    },
    [`& > .${treeItemClasses.root}`]: {
      position: 'relative',
      marginLeft: '12px',
      '&::before': {
        content: '""',
        position: 'absolute',
        left: '-20px',
        top: '50%',
        width: '6px',
        height: '6px',
        backgroundColor: '#93c5fd',
        borderRadius: '50%',
        border: '1px solid white',
        boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
        transform: 'translateY(-50%)',
        zIndex: 1,
      },
    },
  },

  '&[data-level="2"] > .MuiTreeItem-content': {
   margin: '0px'
  },

  // Blue dot for level 1 nodes
  '&[data-level="2"] > .MuiTreeItem-content::before': {
    content: '""',
    position: 'absolute',
    left: '-15px',
    top: '50%',
    width: '4px',
    height: '4px',
    backgroundColor: '#93c5fd',
    borderRadius: '50%',
    transform: 'translateY(-50%)',
    zIndex: 1,
  },
}));

export default function CustomTreeItem({ level = 0, isExpanded = false, ...props }) {
  return (
    <StyledTreeItem 
      {...props} 
      data-level={level}
      className={`${props.className || ''} ${isExpanded ? 'Mui-expanded' : ''}`}
      sx={{
        ...props.sx,
      }}
    />
  );
}